/**
 * Citation processing utilities for OpenAI web search responses.
 * 
 * OpenAI's search models return annotations with url_citation objects
 * that mark character ranges in the text as citations to web sources.
 */

export interface UrlCitation {
  type: "url_citation";
  url: string;
  title: string;
  start_index: number;
  end_index: number;
}

export interface CitationSource {
  index: number;
  url: string;
  title: string;
  hostname: string;
}

export interface ProcessedMessage {
  text: string;
  sources: CitationSource[];
}

/**
 * Extract hostname from a URL for display purposes.
 */
function getHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * Normalize annotations from OpenAI's response format.
 * Handles both nested and flat annotation structures.
 */
export function normalizeAnnotations(raw: any[]): UrlCitation[] {
  if (!raw || !Array.isArray(raw)) return [];

  return raw
    .filter((a) => a?.type === "url_citation")
    .map((a) => {
      // Handle nested format: { type: "url_citation", url_citation: { url, title, ... } }
      const citation = a.url_citation || a;
      return {
        type: "url_citation" as const,
        url: citation.url || "",
        title: citation.title || "",
        start_index: citation.start_index ?? 0,
        end_index: citation.end_index ?? 0,
      };
    })
    .filter((a) => a.url); // Discard entries without a URL
}

/**
 * Process a message's text and annotations to produce:
 * 1. Text with citation spans replaced by superscript markers like [1]
 * 2. A deduplicated list of citation sources
 */
export function processCitations(
  text: string,
  annotations: UrlCitation[]
): ProcessedMessage {
  if (!annotations || annotations.length === 0) {
    return { text, sources: [] };
  }

  // Deduplicate by URL, assign citation numbers
  const urlToIndex = new Map<string, number>();
  const sources: CitationSource[] = [];

  // Sort by start_index to assign citation numbers in reading order
  const sorted = [...annotations].sort((a, b) => a.start_index - b.start_index);

  for (const ann of sorted) {
    if (!urlToIndex.has(ann.url)) {
      const idx = sources.length + 1;
      urlToIndex.set(ann.url, idx);
      sources.push({
        index: idx,
        url: ann.url,
        title: ann.title,
        hostname: getHostname(ann.url),
      });
    }
  }

  // Replace annotated text spans from end to start (to preserve indices)
  let processed = text;
  const reverseSorted = [...sorted].reverse();

  for (const ann of reverseSorted) {
    const citNum = urlToIndex.get(ann.url)!;
    const before = processed.slice(0, ann.start_index);
    const after = processed.slice(ann.end_index);
    // Use a special link format that CitationBadge can detect:
    // [^n^](url) where n is the citation number
    processed = `${before}[^${citNum}^](${ann.url})${after}`;
  }

  return { text: processed, sources };
}

/**
 * Check if a markdown link is a web-citation badge link.
 * Web citation links use the format [^n^](url)
 */
export function isCitationLink(children: React.ReactNode): number | null {
  if (!children) return null;
  const text = String(children);
  const match = text.match(/^\^(\d+)\^$/);
  return match ? parseInt(match[1], 10) : null;
}

// ---------------------------------------------------------------------------
// Inline source citations: [[p:N]] / [[t:SS]] (optional |doc:UUID qualifier)
// ---------------------------------------------------------------------------

export interface SourceCitation {
  /** Stable id used to match a parsed marker back to its badge. */
  key: string;
  kind: "page" | "time" | "doc";
  /** Page number for `page`, integer seconds for `time`, 0 for `doc`. */
  value: number;
  /** Optional end of a contiguous page range (e.g. P.1–P.3). */
  endValue?: number;
  /** Optional content id when the prompt embedded `|doc:UUID` (or for bare doc cites). */
  contentId?: string;
}

const SOURCE_CITATION_RE = /\[\[(p|t):(\d+)(?:\|doc:([0-9a-fA-F-]{8,}))?\]\]/g;

/**
 * Parse a model reply for inline source citations and replace each match with
 * a markdown link of the form `[^pN^]()` / `[^pN-M^]()` / `[^tSS^]()` so the
 * existing markdown renderer can dispatch it to a custom badge component.
 *
 * Consecutive same-doc page citations separated only by punctuation/whitespace
 * are collapsed into a single range badge (e.g. `[[p:1]] [[p:2]] [[p:3]]` →
 * one badge labelled `P.1–P.3` that jumps to page 1).
 *
 * The link href is intentionally empty — navigation is handled in the
 * SourceCitationBadge via the ContentNavigation context.
 */
export function processSourceCitations(text: string): {
  text: string;
  citations: SourceCitation[];
} {
  if (!text) return { text, citations: [] };

  const citations: SourceCitation[] = [];
  const seen = new Map<string, string>(); // dedupe key -> badge key

  // Pass 0 — normalize non-canonical timestamp tokens the model sometimes emits:
  //   [[MM:SS]]                → [[t:SECONDS]]
  //   [[MM:SS]-[MM:SS]]        → [[t:START_SECONDS]]   (jumps to range start)
  //   [[HH:MM:SS]] variants    → [[t:SECONDS]]
  // Done before the canonical parser so badges render even when the model
  // forgets the `t:` prefix or wraps a range in extra brackets.
  const toSeconds = (s: string): number => {
    const parts = s.split(":").map((n) => parseInt(n, 10));
    if (parts.some((n) => !Number.isFinite(n))) return NaN;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0];
  };
  let normalized = text
    // Range form: [[MM:SS]-[MM:SS]] or [[MM:SS] - [MM:SS]]
    .replace(
      /\[\[(\d{1,2}(?::\d{2}){1,2})\]\s*[-–]\s*\[(\d{1,2}(?::\d{2}){1,2})\]\]/g,
      (_m, start: string) => {
        const s = toSeconds(start);
        return Number.isFinite(s) ? `[[t:${s}]]` : _m;
      }
    )
    // Single form: [[MM:SS]] or [[HH:MM:SS]]
    .replace(/\[\[(\d{1,2}(?::\d{2}){1,2})\]\]/g, (_m, ts: string) => {
      const s = toSeconds(ts);
      return Number.isFinite(s) ? `[[t:${s}]]` : _m;
    });

  // Pass 1 — collapse consecutive same-doc page tokens (separated only by
  // whitespace, commas, or "&"/"and") into a single range token.
  // Example: "[[p:3]], [[p:4]] [[p:5]]" → "[[p:3-5]]"
  const RUN_RE =
    /\[\[p:(\d+)(\|doc:[0-9a-fA-F-]{8,})?\]\](?:[\s,]+(?:and\s+|&\s+)?\[\[p:(\d+)\2?\]\])+/g;
  let collapsed = normalized.replace(RUN_RE, (match, _first: string, docPart: string | undefined) => {
    const nums = Array.from(match.matchAll(/\[\[p:(\d+)(?:\|doc:[0-9a-fA-F-]{8,})?\]\]/g))
      .map((m) => parseInt(m[1], 10))
      .filter((n) => Number.isFinite(n));
    if (nums.length < 2) return match;
    // Only collapse if numbers form a strictly contiguous run (1,2,3) — keeps
    // disjoint cites (p3, p7) as separate badges.
    const sorted = [...nums].sort((a, b) => a - b);
    const isContiguous = sorted.every((n, i) => i === 0 || n === sorted[i - 1] + 1);
    if (!isContiguous) return match;
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    return `[[p:${first}-${last}${docPart ?? ""}]]`;
  });

  const RANGE_OR_SINGLE_RE =
    /\[\[(p|t):(\d+)(?:-(\d+))?(?:\|doc:([0-9a-fA-F-]{8,}))?\]\]/g;

  const replaced = collapsed.replace(
    RANGE_OR_SINGLE_RE,
    (_match, kindRaw: string, valueRaw: string, endRaw?: string, docId?: string) => {
      const kind: "page" | "time" = kindRaw === "p" ? "page" : "time";
      const value = parseInt(valueRaw, 10);
      const endValue = endRaw ? parseInt(endRaw, 10) : undefined;
      if (!Number.isFinite(value) || value < 0) return "";
      if (endValue !== undefined && (!Number.isFinite(endValue) || endValue < value)) {
        // Bad range — fall back to single badge.
        return `[[${kindRaw}:${value}${docId ? `|doc:${docId}` : ""}]]`;
      }
      const dedupeKey = `${kind}:${value}:${endValue ?? ""}:${docId ?? ""}`;
      let badgeKey = seen.get(dedupeKey);
      if (!badgeKey) {
        const valuePart = endValue !== undefined ? `${value}-${endValue}` : `${value}`;
        badgeKey = `${kind === "page" ? "p" : "t"}${valuePart}${
          docId ? `_${docId.slice(0, 8)}` : ""
        }`;
        seen.set(dedupeKey, badgeKey);
        citations.push({ key: badgeKey, kind, value, endValue, contentId: docId });
      }
      // Render as markdown link the renderer can intercept.
      return `[^${badgeKey}^]()`;
    }
  );

  // Pass 2 — bare `[[doc:UUID]]` (no page/time qualifier). Collapse runs of the
  // same UUID separated by whitespace/punctuation into a single badge.
  const DOC_RUN_RE =
    /\[\[doc:([0-9a-fA-F-]{8,})\]\](?:[\s,.;:!?]+\[\[doc:\1\]\])+/g;
  let docCollapsed = replaced.replace(DOC_RUN_RE, (_m, id: string) => `[[doc:${id}]]`);
  const DOC_ONLY_RE = /\[\[doc:([0-9a-fA-F-]{8,})\]\]/g;
  docCollapsed = docCollapsed.replace(DOC_ONLY_RE, (_m, docId: string) => {
    const dedupeKey = `doc::${docId}`;
    let badgeKey = seen.get(dedupeKey);
    if (!badgeKey) {
      badgeKey = `d_${docId.slice(0, 8)}`;
      seen.set(dedupeKey, badgeKey);
      citations.push({ key: badgeKey, kind: "doc", value: 0, contentId: docId });
    }
    return `[^${badgeKey}^]()`;
  });

  return { text: docCollapsed, citations };
}

/**
 * Detect a markdown-link child rendered as a source-citation badge marker.
 * Returns the matching badge key (e.g. "p3", "p1-3", "t125", "d_2f81a0ea") or null.
 */
export function isSourceCitationLink(
  children: React.ReactNode
): string | null {
  if (!children) return null;
  const text = String(children);
  const match = text.match(/^\^([pt]\d+(?:-\d+)?(?:_[0-9a-fA-F]+)?|d_[0-9a-fA-F]+)\^$/);
  return match ? match[1] : null;
}
